// @ts-ignore
import * as L from 'leaflet/dist/leaflet-src.esm.js';
import { MapTilerDefaultLayer } from '../layerModel/baseLayers/mapTilerDefaultLayer';
import { OverlayEmptyLayer } from '../layerModel/overlayLayers/overlayEmptyLayer';
// Pane(for aggrupation order z-index) for base layers
export const basePaneName = 'baseLayers';
// Pane(for aggrupation order z-index) for overlay layers
export const overlayPaneName = 'overlayLayers';
/**
 * The LayerFactory class is a factory class that creates
 * a leaflet tile layer based on the type of layer.
 */
export class LayerFactory {
    constructor() {
        // Class that helps in the creation of the default base layers of map tiler
        this.defaultMapTilerHandler = new MapTilerDefaultLayer();
    }
    /**
     * Create a tile layer depending on the type.
     * @param {any} layer - any
     * @returns A new instance of leaflet TileLayer.
     */
    create(layer) {
        let layerObject = null;
        if (layer.type === 'DefaultBaseMapLayer') {
            layerObject = this.createFromDefaultMapTiler(layer);
        }
        if (layer.type === 'GeoServerBaseMapLayer') {
            layerObject = this.createFromGeoServerBaseLayer(layer);
        }
        if (layer.type === 'MaptilerBaseMapLayer') {
            layerObject = this.createFromMapTilerBaseLayer(layer);
        }
        if (layerObject === null)
            throw new Error('Type not exist or not supported');
        return layerObject;
    }
    /**
     * It creates a new leaflet TileLayer object from the given layers options
     * @param {any} overlayLayer - The overlay layer
     * @returns A leaflet TileLayer object
     */
    createOverlayLayer(overlayLayer) {
        const layerObject = [];
        switch (overlayLayer.type) {
            case 'OverlayBasicLayer':
                layerObject.push(this.createFromOverlayBasicLayer(overlayLayer));
                break;
            case 'OverlayCustomLayer':
                layerObject.push(...this.createFromOverlayCustomLayer(overlayLayer));
                break;
            case 'OverlayEmptyLayer':
                layerObject.push(this.createFromOverlayEmptyLayer(overlayLayer));
                break;
            default:
                throw new Error('Type not exist or not supported');
        }
        return layerObject;
    }
    /**
     * Get default options common for all layers
     * @param isMapTiler If the default opts are for the maptiler server
     * @returns default layer options
     */
    defaultOpts(isMapTiler = false) {
        return {
            // Geoserver default tiles are 256x256
            tileSize: isMapTiler ? 512 : 256,
            minZoom: 1,
            maxNativeZoom: 20,
            maxZoom: 20,
            crossOrigin: true,
        };
    }
    /**
     * Create a leaflet tile layer from a string that is the name of
     * a default map tiler layer
     * @param layer the geoServerBaseMapLayer data
     * @returns L.TileLayer
     */
    createFromDefaultMapTiler(layer) {
        const defaultMapOptions = this.defaultMapTilerHandler.getBaseLayer(layer.id);
        const layerOptions = {
            ...this.defaultOpts(true),
            id: layer.id,
            zoomOffset: -1,
            // All base layers in the same pane so they are bellow element layers
            pane: basePaneName,
            attribution: defaultMapOptions.attribution,
        };
        return L.tileLayer(defaultMapOptions.endPoint, layerOptions);
    }
    /**
     * Create a leaflet tile layer from a mapTilerBaseMapLayer
     * @param layer the mapTilerBaseMapLayer data
     * @returns L.TileLayer
     */
    createFromMapTilerBaseLayer(layer) {
        const layerOptions = {
            ...this.defaultOpts(true),
            zoomOffset: -1,
            id: layer.id,
            // All base layers in the same pane so they are bellow element layers
            pane: basePaneName,
            attribution: layer.attribution,
        };
        return L.tileLayer(`${layer.url}?key=${layer.apiKey}`, layerOptions);
    }
    /**
     * Create a leaflet tile layer from a geoServerBaseMapLayer
     * @param layer the geoServerBaseMapLayer data
     * @returns L.TileLayer
     */
    createFromGeoServerBaseLayer(layer) {
        const defaultOpts = this.defaultOpts();
        const layerOptions = {
            ...defaultOpts,
            // All base layers in the same pane so they are bellow element layers
            pane: basePaneName,
            id: layer.id,
            attribution: layer.attribution,
        };
        return L.tileLayer(`${layer.url}&layers=${layer.layers}&viewparams=${layer.viewparams}`, layerOptions);
    }
    /**
     * Create a leaflet wms tile layer from a IOverlayBasicLayer
     * @param layer the overlayBasicLayer data
     * @returns L.TileLayer
     */
    createFromOverlayBasicLayer(layer) {
        const defaultOpts = this.defaultOpts();
        const anyStyle = layer.layers.find((el) => el.style && el.style !== '');
        const anyCQLFilter = layer.layers.find((el) => el.cql_filter && el.cql_filter !== '');
        const anyViewParams = layer.layers.find((el) => el.viewParams && el.viewParams !== '');
        const wmsOptions = {
            ...defaultOpts,
            id: layer.id,
            layers: layer.layers.map((l) => l.layer).join(','),
            format: layer.format && layer.format !== '' ? layer.format : 'image/png',
            version: layer.version,
            pane: overlayPaneName,
            transparent: true,
            cachetime: new Date().getTime(),
        };
        if (anyStyle) {
            wmsOptions.styles = layer.layers.map((l) => l.style).join(',');
        }
        if (anyCQLFilter) {
            wmsOptions.cql_filter = layer.layers.map((l) => l.cql_filter).join(';');
        }
        if (anyViewParams) {
            wmsOptions.viewparams = layer.layers.map((l) => l.viewParams).join(';');
        }
        return L.tileLayer.wms(layer.url, wmsOptions);
    }
    createFromOverlayCustomLayer(layer) {
        const layers = [];
        const defaultOpts = this.defaultOpts();
        layer.layers.forEach((x) => {
            const cqlFilter = x.cql_filter;
            const viewparams = x.viewParams;
            const wmsOptions = {
                ...defaultOpts,
                id: x.id,
                layers: x.layer,
                format: layer.format && layer.format !== '' ? layer.format : 'image/png',
                version: layer.version,
                styles: x.style,
                env: x.env,
                pane: overlayPaneName,
                transparent: true,
            };
            if (cqlFilter) {
                wmsOptions.cql_filter = cqlFilter;
            }
            if (viewparams) {
                wmsOptions.viewparams = viewparams;
            }
            if (x.isVisible) {
                layers.push(L.tileLayer.wms(layer.url, wmsOptions));
            }
        });
        return layers;
    }
    createFromOverlayEmptyLayer(layer) {
        return new OverlayEmptyLayer(layer.id, layer.name, 'OverlayEmptyLayer', layer.isVisible, layer.legend, layer.selectable, layer.opacity, layer.format, layer.subtitle, layer.nameLegend, layer.hidden);
    }
}
//# sourceMappingURL=layerFactory.js.map